import { useIsMounted, useUnmountEffect } from "@react-hookz/web";
import { useRef, useCallback } from "react";

export interface UseRequestAnimationFrameResult {
    /**
     * Cancels the currently scheduled animation frame.
     * If there is no scheduled animation frame, this function does nothing.
     */
    cancel: () => void;

    /**
     * Wraps a function to be called on the next animation frame. Calling this multiple times in the
     * same frame will result in only the latest function being executed. Please wrap whatever
     * function you intend to pass in here in useCallback to avoid unnecessary re-renders.
     */
    raf: (fn: () => void) => void;
}

export const useRequestAnimationFrame = (): UseRequestAnimationFrameResult => {
    const rafIdRef = useRef(0);
    const cancel = useCallback(() => {
        cancelAnimationFrame(rafIdRef.current);
    }, []);

    const isMounted = useIsMounted();

    const raf = useCallback(
        (callback: () => void) => {
            cancel();
            rafIdRef.current = requestAnimationFrame(() => {
                if (isMounted()) {
                    callback();
                }
            });
        },
        [isMounted, cancel]
    );

    useUnmountEffect(cancel);

    return {
        cancel,
        raf,
    };
};
